<template>
  <ContentSection>
    <Container indent="lg-but-on-mobile-default">
      <!-- HEADER -->
      <h2
        v-if="data.heading"
        v-animate-in-from-bottom
        class="mb-3 typo-heading-md sm:mb-4 sm:typo-heading-lg"
        v-html="data.heading"
      />
    </Container>

    <ul class="relative flex w-full snap-x snap-mandatory overflow-x-auto scroll-smooth hide-scrollbar">
      <li
        v-for="video in data.videos"
        :id="video.id"
        :key="video.id"
        class="snap-start -scroll-mt-40"
      >
        <Container indent="lg-but-on-mobile-default" class="w-screen sm:w-[calc(100vw-15px)]">
          <figure class="w-full">
            <!-- VIDEO -->
            <VideoPlayer
              center-video
              :video-url="video.video_url"
              :player-params="{
                controls: 1,
                autoplay: 0,
                muted: 0,
                loop: 0,
                background: 0,
                dnt: 1, // Disable non-essential tracking cookies
              }"

              :play="true"
              :cover-image="video.cover_image"
            />

            <figcaption>
              <span
                v-if="video.title"
                class="sr-only"
              >{{ video.title }}</span>
            </figcaption>
          </figure>
        </Container>
      </li>
    </ul>

    <!-- VIDEO SLIDER -->
    <Container indent="lg-but-on-mobile-default">
      <ul
        v-if="data.videos.length"
        ref="sliderContainer"
        v-drag-scroll
        v-animate-children-in-from-left
        class="mt-5 flex"
      >
        <li v-for="video in data.videos" :key="video.id" class="flex flex-col">
          <div
            class="
              group relative h-full w-64 max-w-[22rem] p-3 transition-colors
              hover:bg-grey-100
              sm:w-full sm:min-w-[17rem]
            "
            :class="{ 'bg-grey-100': video.id === selectedVideoId }"
          >
            <!-- Cover Image -->
            <div class="relative aspect-video w-full">
              <AppImage
                :src="video.cover_image"
                :alt="useMapAltText(video.cover_image)"
                class="size-full object-cover"
                :style="`object-position: ${video.cover_image.focus_css};`"
              />
              <IconPlay
                class="
                  absolute left-1/2 top-1/2 size-[25%] -translate-x-1/2 -translate-y-1/2 text-white
                  transition-transform
                  group-hover:scale-110
                "
              />
            </div>

            <!-- Title -->
            <h3 class="mt-6 typo-heading-sm">
              {{ video.title }}
            </h3>

            <button
              class="before:absolute before:inset-0"
              :aria-label="$t('aria_labels.play_video') + ' ' + video.title"
              type="button"
              @click="onVideoClick(video.id)"
            />
          </div>
        </li>
      </ul>

      <!-- PAGINATION -->

      <PaginationSlider
        class="md:hidden"
        :amount-pages="sliderContainer?.amountPages?.value || 1"
        :active-index="sliderContainer?.activeIndex?.value || 0"
        @scroll-to-item="sliderContainer?.scrollToItem ? sliderContainer?.scrollToItem($event) : null"
      />
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import type { ComponentsMainVideoGalleryFragment } from '#gql'
import type { DragElement } from '~/plugins/dragScroll'

const props = defineProps<{
  data: Enforce<ComponentsMainVideoGalleryFragment>
}>()

const sliderContainer = ref<DragElement | null>(null)
const selectedVideoId = ref<string | null>(null)

if (Array.isArray(props.data.videos) && props.data.videos.length) {
  selectedVideoId.value = props.data.videos[0].id
}

function onVideoClick(videoId: string) {
  selectedVideoId.value = videoId
  const element = document.getElementById(videoId)
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    })
  }
}
</script>

<style scoped>
.w-screen-without-scrollbar {
  width: calc(100vw - var(--scrollbar-width));
}
</style>
